import React, { useMemo } from 'react';
import { PropertyMediaKind, FileStatus, type TCFile } from 'src/api/generated';
import {
  PiEye,
  PiFile,
  PiImageSquare,
  PiMusicNotes,
  PiVideoCamera,
} from 'react-icons/pi';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import Switch from 'src/modules/shared/components/atoms/switch';
import { Text } from 'src/modules/shared/components/atoms/text';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import { StatusPill } from 'src/modules/shared/components/atoms/status-pill';
import { useFilesMetadata } from 'src/modules/shared/hooks/api/files';
import {
  inferPropertyMediaKindFromFile,
  type SupportedPropertyMediaKind,
} from 'src/modules/shared/utils/file/media-kind.util';

interface MediaThumbnailSelectorItem {
  fileId?: string;
  kind?: SupportedPropertyMediaKind;
}

interface MediaThumbnailSelectorProps {
  items: MediaThumbnailSelectorItem[];
  selectedFileIds: string[];
  onSelectedFileIdsChange?: (nextFileIds: string[]) => void;
  title: string;
  description: string;
  emptyLabel: string;
  selectable?: boolean;
}

const isReadyFile = (file: TCFile | null): file is TCFile =>
  file != null && file.status === FileStatus.READY;

const mediaKindIconByKind: Record<SupportedPropertyMediaKind, React.ReactNode> =
  {
    IMAGE: <PiImageSquare className="h-6 w-6" />,
    VIDEO: <PiVideoCamera className="h-6 w-6" />,
    AUDIO: <PiMusicNotes className="h-6 w-6" />,
    DOCUMENT: <PiFile className="h-6 w-6" />,
  };

/**
 * Compact media selector for publication-specific reuse of property media.
 */
export const MediaThumbnailSelector: React.FC<MediaThumbnailSelectorProps> = ({
  items,
  selectedFileIds,
  onSelectedFileIdsChange,
  title,
  description,
  emptyLabel,
  selectable = true,
}) => {
  const normalizedItems = useMemo(
    () =>
      items.flatMap((item) =>
        item.fileId
          ? [
              {
                fileId: item.fileId,
                kind: item.kind ?? PropertyMediaKind.IMAGE,
              },
            ]
          : []
      ),
    [items]
  );
  const fileIds = useMemo(
    () => normalizedItems.map((item) => item.fileId),
    [normalizedItems]
  );
  const { files, loading } = useFilesMetadata(fileIds);

  const readyFiles = useMemo(() => {
    const mappedFiles = fileIds
      .map((fileId) => files.find((file) => file.id === fileId) ?? null)
      .filter(isReadyFile);

    return mappedFiles.map((file) => ({
      file,
      fallbackKind:
        normalizedItems.find((item) => item.fileId === file.id)?.kind ??
        PropertyMediaKind.IMAGE,
    }));
  }, [fileIds, files, normalizedItems]);

  const handleToggle = (fileId: string) => {
    if (!onSelectedFileIdsChange) {
      return;
    }

    onSelectedFileIdsChange(
      selectedFileIds.includes(fileId)
        ? selectedFileIds.filter((currentFileId) => currentFileId !== fileId)
        : [...selectedFileIds, fileId]
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Text size="small" weight="medium" className="text-slate-900">
          {title}
        </Text>
        <Text size="tiny" className="text-slate-500">
          {description}
        </Text>
      </div>

      {loading ? (
        <div className="flex gap-3 overflow-hidden">
          {Array.from({ length: Math.max(fileIds.length, 3) }).map(
            (_, index) => (
              <TextSkeleton
                key={`media-thumb-skeleton-${index}`}
                className="h-24 w-24 shrink-0 rounded-xl"
              />
            )
          )}
        </div>
      ) : readyFiles.length > 0 ? (
        <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1">
          {readyFiles.map(({ file, fallbackKind }, index) => {
            const mediaKind = inferPropertyMediaKindFromFile(
              file,
              fallbackKind
            );
            const tooltipContent = (
              <div className="max-w-52">
                <p className="font-medium">{file.fileName}</p>
                <p className="mt-1 text-xs opacity-90">{file.contentType}</p>
              </div>
            );

            return (
              <Tooltip
                key={file.id}
                content={tooltipContent}
                placement="top"
                size="xs"
              >
                <div className="w-24 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <div className="relative aspect-square bg-slate-100">
                    {mediaKind === PropertyMediaKind.IMAGE && file.url ? (
                      <img
                        src={file.url}
                        alt={file.fileName}
                        className="h-full w-full object-cover"
                      />
                    ) : mediaKind === PropertyMediaKind.VIDEO && file.url ? (
                      <video
                        className="h-full w-full object-cover"
                        muted
                        playsInline
                        preload="metadata"
                      >
                        <source src={file.url} type={file.contentType} />
                      </video>
                    ) : (
                      <div className="flex h-full items-center justify-center text-slate-500">
                        {mediaKindIconByKind[mediaKind]}
                      </div>
                    )}

                    <div className="absolute left-1.5 top-1.5">
                      <StatusPill
                        variant="secondary"
                        label={`#${index + 1}`}
                        className="px-2.5 py-0.5"
                      />
                    </div>

                    {selectable ? (
                      <div className="absolute right-1.5 top-1.5 flex items-center justify-center rounded-full bg-white/95 px-0 py-px shadow-sm backdrop-blur-sm">
                        <div className="origin-center scale-[0.85]">
                          <Switch
                            checked={selectedFileIds.includes(file.id)}
                            onChange={() => handleToggle(file.id)}
                            size="xs"
                            className="shrink-0"
                          />
                        </div>
                      </div>
                    ) : null}

                    {file.url ? (
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Preview media"
                        className="absolute bottom-1.5 left-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/95 text-slate-600 shadow-sm transition hover:text-slate-900"
                      >
                        <PiEye className="h-3.5 w-3.5" />
                      </a>
                    ) : null}
                  </div>
                </div>
              </Tooltip>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center">
          <Text size="small" className="text-slate-500">
            {emptyLabel}
          </Text>
        </div>
      )}
    </div>
  );
};
